import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  Psychology as PsychologyIcon
} from '@mui/icons-material';
import * as clinicalSupervisorApi from '../../services/clinicalSupervisorApi';
import ClientDetailView from '../therapist/ClientDetailView';
import TassPanel from '../therapist/tass/TassPanel';
import { useSocket } from '../../context/SocketContext';

/**
 * Wrapper component that allows clinical supervisors to view client details
 * in the context of supervising a therapist. This component provides:
 * - Visual indicators that this is a supervisor view
 * - Information about which therapist's client is being viewed
 * - Passes appropriate API context to ClientDetailView
 */
function SupervisorClientView({ token, user }) {
  const navigate = useNavigate();
  const { therapistId, clientId } = useParams();
  const socket = useSocket(); // Get socket from context using hook

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [client, setClient] = useState(null);
  const [therapist, setTherapist] = useState(null);
  const [tassOpen, setTassOpen] = useState(false);
  const [formulationAudit, setFormulationAudit] = useState(null);
  const [formulationLoading, setFormulationLoading] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch both client and therapist details
      const [clientData, therapistData] = await Promise.all([
        clinicalSupervisorApi.getClientDetails(clientId, token),
        clinicalSupervisorApi.getTherapistDetails(therapistId, token)
      ]);

      setClient(clientData.client);
      setTherapist(therapistData.therapist);
    } catch (err) {
      console.error('Error loading supervisor client view:', err);
      setError(err.response?.data?.msg || 'Failed to load client details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [clientId, therapistId, token]);

  const loadFormulationAudit = async () => {
    if (formulationAudit) return;
    try {
      setFormulationLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/clinical-supervisor/gen2/formulation-audit/${clientId}`,
        { headers: { 'x-auth-token': token } }
      );
      if (!response.ok) throw new Error('Failed to fetch formulation audit');
      const data = await response.json();
      setFormulationAudit(data.traces || []);
    } catch (err) {
      console.error('Error loading formulation audit:', err);
      setFormulationAudit([]);
    } finally {
      setFormulationLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/clinical-supervisor/therapists/${therapistId}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <IconButton onClick={handleBack}>
          <BackIcon />
        </IconButton>
      </Box>
    );
  }

  if (!client || !therapist) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Client or therapist not found
        </Alert>
        <IconButton onClick={handleBack}>
          <BackIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{
        p: 3,
        marginRight: tassOpen ? '380px' : '48px',
        transition: 'margin-right 0.3s'
      }}>
        {/* Supervisor Context Banner */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: 'info.lighter',
            border: '1px solid',
            borderColor: 'info.main',
            borderRadius: 1
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Tooltip title="Back to Therapist Details">
                <IconButton onClick={handleBack} size="small">
                  <BackIcon />
                </IconButton>
              </Tooltip>
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Chip
                    label="SUPERVISOR VIEW"
                    color="info"
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Viewing as Clinical Supervisor
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Therapist: <strong>{therapist.name}</strong> ({therapist.email})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Supervisor: {user.email}
                </Typography>
              </Box>
            </Box>
            <Box textAlign="right">
              <Typography variant="caption" color="success.main" display="block" fontWeight="bold">
                Full Access Mode
              </Typography>
              <Typography variant="caption" color="text.secondary">
                All actions are logged for compliance
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Gen2 Formulation Audit */}
        <Accordion
          sx={{ mb: 2 }}
          onChange={(e, expanded) => { if (expanded) loadFormulationAudit(); }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <PsychologyIcon color="action" />
              <Typography variant="subtitle1" fontWeight="bold">Gen2 Formulation Audit</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {formulationLoading ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : formulationAudit && formulationAudit.length > 0 ? (
              <Box>
                {formulationAudit.map((trace, i) => (
                  <Box key={i} sx={{ mb: 2, pl: 2, borderLeft: '3px solid', borderColor: trace.formulationSnapshot?.wasUpdatedThisTurn ? 'primary.main' : 'grey.300' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {(trace.createdAt || trace.timestamp) ? new Date(trace.createdAt || trace.timestamp).toLocaleString() : `Turn ${i + 1}`}
                      </Typography>
                      <Chip
                        label={trace.processingMode?.replace('_', ' ') || 'unknown'}
                        size="small"
                        sx={{
                          fontSize: '10px',
                          height: 20,
                          textTransform: 'capitalize',
                          backgroundColor: trace.processingMode === 'consultation' ? '#e1bee7' :
                            trace.processingMode === 'deep_thinking' ? '#bbdefb' : '#e0e0e0'
                        }}
                      />
                      {trace.formulationSnapshot?.wasUpdatedThisTurn && (
                        <Chip label={`v${trace.formulationSnapshot.version}`} size="small" color="primary" sx={{ fontSize: '10px', height: 20 }} />
                      )}
                    </Box>
                    {trace.triggerReason && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Trigger: {trace.triggerReason}
                      </Typography>
                    )}
                    {trace.formulationSnapshot?.activeDomains?.length > 0 && (
                      <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                        {trace.formulationSnapshot.activeDomains.map((domain, j) => (
                          <Chip key={j} label={domain} size="small" variant="outlined" sx={{ fontSize: '10px', height: 20 }} />
                        ))}
                      </Box>
                    )}
                    {trace.formulationSnapshot?.transdiagnosticProcesses?.length > 0 && (
                      <Box mt={0.5}>
                        {trace.formulationSnapshot.transdiagnosticProcesses.map((proc, j) => (
                          <Typography key={j} variant="caption" display="block" sx={{ fontSize: '10px' }}>
                            {proc.process}: intensity {proc.intensity}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {i < formulationAudit.length - 1 && <Divider sx={{ mt: 1.5 }} />}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No Gen2 formulation data available for this client
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Pass client to ClientDetailView with supervisor context */}
        <ClientDetailView
          selectedClient={client}
          onBack={handleBack}
          socket={socket}
          token={token}
          user={user}
          viewMode="supervisor"
          apiContext="supervisor"
          therapistInfo={{
            id: therapist._id,
            name: therapist.name,
            email: therapist.email
          }}
          supervisorInfo={{
            id: user.id,
            name: user.name,
            email: user.email
          }}
        />
      </Box>

      {/* TASS Sidebar — proxied through the client's therapist */}
      <TassPanel
        token={token}
        open={tassOpen}
        onToggle={() => setTassOpen(!tassOpen)}
        viewContext={{
          activeView: 'clients',
          selectedClientId: client?._id,
          selectedClientName: client?.name
        }}
        proxyTherapistId={therapistId}
        onNavigateToClient={(navClientId, clientName) => {
          navigate(`/clinical-supervisor/therapists/${therapistId}/clients/${navClientId}`);
        }}
      />
    </Box>
  );
}

export default SupervisorClientView;
